import * as React from 'react';
import { useEffect, useRef } from 'react';
import { observer } from 'mobx-react';

import { Viewport, ViewSmall, ViewLarge } from 'bernie-view';
import { UitkButtonSize } from 'uitk-react-button';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkErrorSummary } from 'uitk-react-error-summary';

import { trackAnalytics, analyticsConfigMapper, trackSearchFormAnalytics } from 'utility';
import { SearchFormButton, Journey } from '.';
import { ChooseActionButton } from 'components/shared';
import { FlightsSearchFormJourneyCriteriaType, FlightsSearchFormRemoveJourneyActionType } from 'types/search/wizard';
import { useStores } from 'utility/hooks';
import { focusElement } from 'utility/focus-element';
import { trackJourneyAnalytics } from 'utility';
import { FlightsTripType, FlightsActionType } from '__generated__/api';
import { InteractableFieldComponentProps } from 'types/shared';

export interface SearchTripControlsProps extends InteractableFieldComponentProps {
  essEndpoint?: string;
}

export const SearchTripControls = observer(({ essEndpoint, onFieldInteraction }: SearchTripControlsProps) => {
  const { searchFormStore, analytics, context } = useStores();
  const { searchForm, journeyIds, shouldRenderErrorSummary } = searchFormStore;
  const { addJourneyAction, journeys } = searchForm;
  const { tripType } = searchFormStore.selectedTripTypeOptionItem;
  const isMultiDest = tripType === FlightsTripType.MULTI_DESTINATION;
  const disabledAddJourneyButton = searchForm.journeys.length >= searchFormStore.MAX_NUMBER_JOURNEYS;
  const firstErrorRef = searchFormStore.firstErrorRef;
  const firstErrorRefId =
    (firstErrorRef && firstErrorRef.current && firstErrorRef.current.props && firstErrorRef.current.props.stid) || '';
  const errorsSummaryRef = useRef(null);
  const errorsSummaryLinkAnalyticsConfig = {
    linkName: searchForm.errorSummary && searchForm.errorSummary.linkAnalytics.linkName,
    rfrr: searchForm.errorSummary && searchForm.errorSummary.linkAnalytics.referrerId
  };
  const errorsSummaryLinkAnalytics = analyticsConfigMapper(errorsSummaryLinkAnalyticsConfig, analytics);
  const errorsSummaryDisplayAnalyticsConfig = {
    linkName: searchForm.errorSummary && searchForm.errorSummary.displayAnalytics.linkName,
    rfrr: searchForm.errorSummary && searchForm.errorSummary.displayAnalytics.referrerId
  };

  const handleRemoveJourney = (journeyIndex: number) => {
    onFieldInteraction && onFieldInteraction();
    searchFormStore.removeJouney(journeyIndex, (removeJourneyAction: FlightsSearchFormRemoveJourneyActionType) => {
      trackJourneyAnalytics(analytics, removeJourneyAction.analytics, journeyIndex + 1);
    });
  };

  const handleAddJourney = (flightsActionType: FlightsActionType): void => {
    if (flightsActionType === FlightsActionType.SEARCH_FORM_ADD_JOURNEY) {
      onFieldInteraction && onFieldInteraction();
      searchFormStore.addNewBlankJourney();
      searchFormStore.shouldFocusLastAddedTrip = true;
      trackJourneyAnalytics(analytics, addJourneyAction.analytics, searchForm.journeys.length);
    }
  };

  const updateJourneyDates = (journeyIndex: number, selectedDepartureDate: Date, selectedReturnDate: Date): void => {
    const journey = searchForm.journeys[journeyIndex];
    const dateAnalytics = selectedReturnDate ? journey.returnDate.analytics : journey.departureDate.analytics;

    searchFormStore.setJourneyDates(journeyIndex, selectedDepartureDate, selectedReturnDate);
    trackSearchFormAnalytics(
      analytics,
      dateAnalytics,
      searchFormStore.selectedTripTypeOptionItem.tripType,
      journeyIndex + 1
    );
  };

  const flightLabel = (template: string, journeyNumber: number): string => {
    return template.replace('{flightNumber}', journeyNumber.toString());
  };

  useEffect(() => {
    setTimeout(() => {
      if (shouldRenderErrorSummary && !firstErrorRef) {
        focusElement(errorsSummaryRef);
        trackAnalytics(errorsSummaryDisplayAnalyticsConfig, analytics);
      } else if (!shouldRenderErrorSummary && firstErrorRef) {
        focusElement(firstErrorRef);
      }
    }, 250);
  }, [analytics, errorsSummaryDisplayAnalyticsConfig, shouldRenderErrorSummary, errorsSummaryRef, firstErrorRef]);

  return (
    <Viewport>
      <ViewSmall>
        <UitkLayoutGrid space="two" columns={1} className="all-y-padding-three">
          {shouldRenderErrorSummary && (
            <UitkErrorSummary
              heading={searchForm.errorSummary.header}
              link={searchForm.errorSummary.linkText}
              linkClickFocus={firstErrorRef}
              linkClickFocusId={firstErrorRefId}
              domRef={errorsSummaryRef}
              analytics={errorsSummaryLinkAnalytics}
            />
          )}

          {journeys.map((journey: FlightsSearchFormJourneyCriteriaType, index: number) => (
            <React.Fragment key={`search-form-journey-${journeyIds[index]}`}>
              {isMultiDest && journey.label && (
                <UitkLayoutGridItem colSpan={1}>
                  <UitkLayoutFlex justifyContent="space-between" alignItems="center">
                    <UitkLayoutFlexItem className="uitk-type-heading-400">
                      <legend data-test-id={`flight-number-${index + 1}`}>
                        {flightLabel(journey.label.template, index + 1)}
                      </legend>
                    </UitkLayoutFlexItem>
                  </UitkLayoutFlex>
                </UitkLayoutGridItem>
              )}
              <UitkLayoutGridItem colSpan={1}>
                <div>
                  <Journey
                    key={`journey${index}`}
                    journey={journey}
                    index={index}
                    context={context}
                    flightType={tripType}
                    isMobile={true}
                    essEndpoint={essEndpoint}
                    totalJourneys={journeys.length}
                    updateJourneyDates={updateJourneyDates}
                    deleteFlight={handleRemoveJourney}
                    onFieldInteraction={onFieldInteraction}
                  />
                </div>
              </UitkLayoutGridItem>
            </React.Fragment>
          ))}
          {isMultiDest && (
            <>
              <hr className="all-y-margin-zero" />
              <UitkLayoutGridItem colSpan={1}>
                <UitkLayoutFlex>
                  <ChooseActionButton
                    action={addJourneyAction}
                    isFullWidth={false}
                    handleActionSelection={handleAddJourney}
                    size={UitkButtonSize.LARGE}
                    disabled={disabledAddJourneyButton}
                    testDataId="add-journey-trigger"
                    shouldNotTriggerAnalytics={true}
                  />
                </UitkLayoutFlex>
              </UitkLayoutGridItem>
            </>
          )}
        </UitkLayoutGrid>
      </ViewSmall>
      <ViewLarge>
        <UitkLayoutGrid space="four" columns={1} className="all-y-margin-two">
          {shouldRenderErrorSummary && (
            <UitkErrorSummary
              heading={searchForm.errorSummary.header}
              link={searchForm.errorSummary.linkText}
              linkClickFocus={firstErrorRef}
              linkClickFocusId={firstErrorRefId}
              domRef={errorsSummaryRef}
              analytics={errorsSummaryLinkAnalytics}
            />
          )}
          {journeys.map((journey: FlightsSearchFormJourneyCriteriaType, index: number) => (
            <React.Fragment key={index}>
              {isMultiDest && journey.label && (
                <UitkLayoutFlex justifyContent="space-between" alignItems="center">
                  <UitkLayoutFlexItem className="uitk-type-heading-400">
                    <legend data-test-id={`flight-number-${index + 1}`}>
                      {flightLabel(journey.label.template, index + 1)}
                    </legend>
                  </UitkLayoutFlexItem>
                </UitkLayoutFlex>
              )}
              <UitkLayoutGridItem colSpan={1} key={`search-form-journey-${journeyIds[index]}`}>
                <div>
                  <Journey
                    key={`journey${index}`}
                    journey={journey}
                    index={index}
                    context={context}
                    flightType={tripType}
                    essEndpoint={essEndpoint}
                    totalJourneys={journeys.length}
                    updateJourneyDates={updateJourneyDates}
                    deleteFlight={handleRemoveJourney}
                    onFieldInteraction={onFieldInteraction}
                  />
                </div>
              </UitkLayoutGridItem>
            </React.Fragment>
          ))}
          {isMultiDest && (
            <>
              <hr className="all-y-margin-zero" />
              <UitkLayoutFlex justifyContent="space-between" alignItems="center" className="all-x-margin-zero">
                <UitkLayoutFlexItem>
                  <ChooseActionButton
                    action={addJourneyAction}
                    isFullWidth={false}
                    handleActionSelection={handleAddJourney}
                    size={UitkButtonSize.LARGE}
                    disabled={disabledAddJourneyButton}
                    testDataId="add-journey-trigger"
                    shouldNotTriggerAnalytics={true}
                  />
                </UitkLayoutFlexItem>
              </UitkLayoutFlex>
              <UitkLayoutGrid columns={4}>
                <UitkLayoutGridItem colSpan={1}>
                  <SearchFormButton onFieldInteraction={onFieldInteraction} />
                </UitkLayoutGridItem>
              </UitkLayoutGrid>
            </>
          )}
        </UitkLayoutGrid>
      </ViewLarge>
    </Viewport>
  );
});
